/**
 * What a typed letter becomes: the pitch, and the place on the instrument it is taken at.
 *
 * One keystroke means three different things on three kinds of staff. On an ordinary staff
 * `G` is a pitch; on a drum staff it is whichever drum the kit writes nearest that line; on
 * a tab staff it is a pitch and the lowest fret that can play it. Keeping the three in one
 * place is what lets the entry machine stay about carets, durations and bars, and it is why
 * nothing above here has to ask what kind of staff it is writing on.
 */

import { drumAt } from "../document/percussion";
import type { PercussionKit } from "../document/percussion";
import { drumHeadOf, toSounding, toWritten } from "../document/pitch";
import type { NotePitch, Step, Transposition } from "../document/pitch";
import { positionFor } from "../document/tab";
import type { StringTuning } from "../document/tab";
import type { MeasureId, PartId } from "../document/ids";
import { instrumentAt, measureIndexOf } from "../document/query";
import type { Score } from "../document/types";

/**
 * What the staff under the caret makes of a typed letter.
 *
 * Three facts, gathered once, because a letter means something different on each kind of
 * staff and the machine above should not have to ask which kind it is writing on.
 */
export interface TypedStaff {
  /** The kit, on a drum staff: a letter there chooses a drum rather than a pitch. */
  readonly kit: PercussionKit | null;
  /** The open strings, on a tab staff, so a typed letter is given a place to be played at. */
  readonly tuning: readonly StringTuning[] | null;
  /**
   * The transposition to take back out of a typed letter, or `null` when a letter names the
   * pitch that is stored.
   *
   * It is not `null` only while a concert-pitch score is being looked at: there the letters
   * on the page are what is heard, so a `C` typed into a clarinet part is a written D and
   * has to be stored as one. The octave a part is written in is not part of it -- the
   * concert reading leaves that where it is, so neither does this.
   */
  readonly heard: Transposition | null;
}

/** A letter typed, with an accidental and an octave when the caller knows them. */
export interface Typed {
  readonly step: Step;
  readonly alter?: number;
  readonly octave?: number;
}

const LETTERS: readonly Step[] = ["C", "D", "E", "F", "G", "A", "B"];

/** A pitch as one number, so that two of them can be compared. */
function height(step: Step, octave: number): number {
  return octave * 7 + LETTERS.indexOf(step);
}

/**
 * The octave a bare letter should sound in: the one that puts it nearest the note before.
 *
 * Typing letters is only usable if `C` after a `B` means the C above rather than the C a
 * seventh below. With nothing written yet there is nothing to be near, so the register the
 * caller is in is used.
 */
export function octaveFor(last: Step | null, register: number, step: Step): number {
  if (last === null) return register;
  const from = height(last, register);
  let best = register;
  let distance = Number.POSITIVE_INFINITY;
  for (const octave of [register - 1, register, register + 1]) {
    const gap = Math.abs(height(step, octave) - from);
    if (gap < distance) {
      best = octave;
      distance = gap;
    }
  }
  return best;
}

/** The note one typed letter means on the staff the caret is on. */
export function typedPitch(
  typed: Typed,
  octave: number,
  tieStop: boolean,
  staff: TypedStaff,
): NotePitch {
  const { kit, tuning, heard } = staff;
  // On a drum staff a letter chooses a drum rather than a pitch, and the head goes where
  // that drum is written -- which may not be the line the letter names, because a kit does
  // not have an instrument on every line.
  const drum = kit === null ? null : drumAt(kit, typed.step, octave);
  if (drum !== null) return { ...drumHeadOf(drum.step, drum.octave, drum.name), tieStop };

  const named: NotePitch = {
    step: typed.step,
    alter: typed.alter ?? 0,
    octave,
    accidental: "auto",
    tieStart: false,
    tieStop,
    drum: null,
    tab: null,
  };
  const pitch = heard === null ? named : toWritten(named, heard);
  // A letter typed on a tab staff is still a note: it is given the lowest place on the
  // instrument that can play it, which is the position a player keeps or corrects.
  return tuning === null ? pitch : { ...pitch, tab: positionFor(tuning, pitch) };
}

/** A stored pitch read back in the letters it would have been typed as. */
export function asTyped(pitch: NotePitch, staff: TypedStaff): NotePitch {
  return staff.heard === null || pitch.drum !== null ? pitch : toSounding(pitch, staff.heard);
}

/** The strings a part is written on, or `null` on a staff of pitches. */
export function tuningAt(score: Score, partId: PartId | null): readonly StringTuning[] | null {
  if (partId === null) return null;
  return score.parts.find((part) => part.id === partId)?.instrument.tuning ?? null;
}

/** The kit a part is written on, or `null` on an ordinary staff. */
export function kitAt(score: Score, partId: PartId | null): PercussionKit | null {
  if (partId === null) return null;
  return score.parts.find((part) => part.id === partId)?.instrument.percussion ?? null;
}

/**
 * Everything a typed letter is read against on one part's staves.
 *
 * `concert` says the letters name what is heard rather than what is written, which is true
 * while a concert-pitch score is on the screen. A fretted part is never read that way: its
 * numbers are frets against a tuning stated in written pitch, and the concert reading leaves
 * such a part exactly as it stands -- see `document/concert.ts`.
 */
export function staffAt(
  score: Score,
  partId: PartId | null,
  measureId: MeasureId | null,
  concert: boolean,
): TypedStaff {
  const tuning = tuningAt(score, partId);
  const transposition = partId === null ? null : transpositionAt(score, partId, measureId);
  const chromatic = transposition?.chromatic ?? 0;
  const diatonic = transposition?.diatonic ?? 0;
  const moved = concert && tuning === null && (chromatic !== 0 || diatonic !== 0);
  return {
    kit: kitAt(score, partId),
    tuning,
    heard: moved ? { chromatic, diatonic, octaveChange: 0 } : null,
  };
}

/**
 * What the part reads at in that bar, which is not always what the part reads at.
 *
 * A player who has picked another instrument up reads at the one in their hands, so a letter
 * typed into a concert score after a doubling is written for the instrument playing it. With
 * no bar to go on -- nothing selected, nothing under the caret -- the part's own is used.
 */
function transpositionAt(
  score: Score,
  partId: PartId,
  measureId: MeasureId | null,
): Transposition | null {
  const part = score.parts.find((candidate) => candidate.id === partId);
  if (part === undefined) return null;
  const index = measureId === null ? -1 : measureIndexOf(score, measureId);
  return index < 0 ? part.instrument.transposition : instrumentAt(score, part, index).transposition;
}
